import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

export interface BreadcrumbItem {
  label?: string;
  translateKey?: string;
  route?: string | string[];
  fragment?: string;
  queryParams?: any;
  clickHandler?: () => void;
  isActive?: boolean;
  isHtml?: boolean;
  usePipe?: 'multiLang' | 'ml' | 'translate';
  condition?: boolean;
  cssClasses?: string[];
}

export interface BreadcrumbConfig {
  items: BreadcrumbItem[];
  showMobileBackButton?: boolean;
  wrapperClass?: string;
  breadcrumbClasses?: string[];
  responsiveHidden?: boolean;
  responsiveClasses?: {
    wrapper?: string;
    nav?: string;
    button?: string;
  };
  ariaLabel?: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit {
  @Input() config: BreadcrumbConfig;

  constructor(
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (!this.config || !this.config.items) {
      console.warn('BreadcrumbComponent: Valid configuration with items required');
    }
  }

  get visibleItems(): BreadcrumbItem[] {
    if (!this.config || !this.config.items) {
      return [];
    }
    return this.config.items.filter(item => item.condition !== false);
  }

  handleClick(item: BreadcrumbItem, event: Event): void {
    if (item.clickHandler) {
      event.preventDefault();
      item.clickHandler();
    }
  }

  goBack(): void {
    window.history.back();
  }

  isClickable(item: BreadcrumbItem, isLast: boolean): boolean {
    return !item.isActive && !isLast && (!!item.route || !!item.clickHandler);
  }

  isCurrentPage(item: BreadcrumbItem, isLast: boolean): boolean {
    return item.isActive || isLast;
  }

  getWrapperClasses(): string {
    const classes: string[] = [];

    if (this.config.wrapperClass) {
      classes.push(this.config.wrapperClass);
    }

    if (this.config.responsiveClasses && this.config.responsiveClasses.wrapper) {
      classes.push(this.config.responsiveClasses.wrapper);
    } else if (this.config.responsiveHidden) {
      classes.push('d-none d-md-block');
    }

    return classes.join(' ');
  }

  getNavClasses(): string {
    const classes: string[] = [];

    if (this.config.responsiveClasses && this.config.responsiveClasses.nav) {
      classes.push(this.config.responsiveClasses.nav);
    }

    return classes.join(' ');
  }

  getBreadcrumbClasses(): string {
    const classes: string[] = ['breadcrumb'];

    if (this.config.breadcrumbClasses) {
      classes.push(...this.config.breadcrumbClasses);
    }

    return classes.join(' ');
  }

  getButtonClasses(): string {
    const classes: string[] = ['d-md-none', 'mt-3', 'pl-1', 'mb-2'];

    if (this.config.responsiveClasses && this.config.responsiveClasses.button) {
      classes.push(this.config.responsiveClasses.button);
    }

    return classes.join(' ');
  }

  getItemClasses(item: BreadcrumbItem, isLast: boolean): string {
    const classes: string[] = ['breadcrumb-item'];

    if (item.isActive || isLast) {
      classes.push('active');
    }

    if (item.cssClasses) {
      classes.push(...item.cssClasses);
    }

    return classes.join(' ');
  }

  getAriaLabel(): string {
    return this.config.ariaLabel || 'breadcrumb';
  }
}
